import React, { useEffect, useMemo, useState, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { Link, matchPath, useHistory, useLocation } from 'react-router-dom';

import { ReactComponent as LogoIconLightBg } from '../../media/images/LogoIconLightBg.svg';
import { ReactComponent as RaiseRightLogo } from '../../media/images/RaiseRightLogo.svg';
import { ReactComponent as FlipGiveIcon } from '../../media/images/flipgive/FlipGiveIcon.svg';
import { ReactComponent as FlipGiveLogo } from '../../media/images/flipgive/FlipGiveLogo.svg';
import { Branding, IS_FLIPGIVE } from 'common/env';
import { ExternalLinks } from 'externalLinks';

import { SearchBar } from './SearchBar';
import NavButtons from '../nav/NavButtons';

import { useRootSelector } from 'rootStore';
import { createStyles } from 'utils/createStyle';
import { applyTerm, updateSearch } from 'common/features/store/duck/search/duck';
import { getPath, Routes } from 'routes';
import {
    selectHomeDetailsAsyncState,
    selectHomeDetails,
} from 'common/features/store/duck/home/duck';
import { OrgSelectDropdown } from './components/OrgSelectDropdown';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import Cookies from 'universal-cookie';
import { ShopNavOptions } from './components/ShopNavOptions';
import {
    FeatureFlagTypes,
    selectAffiliateEnabledWeb,
    selectIsP2PEnabled,
} from 'common/features/store/duck/ffs';
import { UnAuthMobileMenu } from './components/UnAuthMobileMenu';
import { ArrowUpRight, ChevronDown } from 'react-feather';
import { colors } from 'styles/settings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/pro-light-svg-icons';
import { P2pSources } from 'features/account-settings/components/SettingsTab/InviteFriendsFamily';
import { FeaturesState } from 'common/features/featuresReducer';
import { HomeDetailsResponse } from 'common/api/crm/home/models/HomeDetailsResponse';
import { gaLogClickTopBanner } from 'config/analytics';

const styles = createStyles({
    container: [
        'shadow-lg pb-2',
        {
            '@media (min-width: 768px)': {
                minHeight: '100px',
            },
        },
    ],
    headerContainer: [
        'grid px-4 md:px-8 pt-4 pb-0 gap-x-0 md:gap-x-4 justify-items-end md:justify-items-stretch items-center',
        {
            gridTemplateColumns: 'max-content auto max-content',
        },
    ],
    headerLink: [
        'flex items-center gap-1 text-brand text-base font-normal px-3 md:text-16px md:font-semibold md:px-4 no-underline xl:truncate',
    ],
    logoContainer: ['w-auto flex'],
    unAuthBigLogo: ['w-auto block', { '@media (min-width: 600px)': { paddingRight: '1.25rem' } }],
    authSmallLogo: ['w-full block lg:hidden'],
    authBigLogo: ['w-full hidden lg:block', { minWidth: '244px' }],
    logoSize: ['block', { width: '203px', height: '36px' }],
    flipGiveSmallIconSize: ['block', { width: '50px', height: '50px' }],
    flipGiveLogoSize: [
        'block',
        { width: '151px', height: '36px' },
        { '@media (min-width: 600px)': { marginLeft: '-15px' } },
    ],
    unauthHeaderContainer: [
        'flex px-4 md:px-5 pt-4 pb-3 justify-center',
        {
            gridTemplateColumns: 'max-content auto max-content',
            alignItems: 'center',
        },
        { '@media (min-width: 600px)': { justifyContent: 'space-between' } },
    ],
    unAuthItemsContainer: [
        'items-center hidden',
        { '@media (min-width: 1140px)': { display: 'flex' } },
    ],
    signUpContainer: ['hidden', { '@media (min-width: 600px)': { display: 'flex' } }],
    mobileSignUpContainer: ['flex', { '@media (min-width: 600px)': { display: 'none' } }],
    mobileSignUpLinkContainer: [
        'flex items-center w-full lg:w-auto pr-4 xl:pr-4',
        { '@media (min-width: 600px)': { width: 'auto' } },
    ],
    signUpLink: [
        'bg-brand rounded-full text-white font-semibold flex items-center text-center no-underline text-base px-6 py-2 mx-auto w-full justify-center',
        { minWidth: 'max-content' },
        { '@media (min-width: 600px)': { width: 'auto', justifyContent: 'center' } },
    ],
    navOptionsContainer: [
        'flex justify-end lg:px-8 pt-2',
        { '@media (min-width: 1024px)': { borderTop: '1px solid lightGrey' } },
    ],
    navBar: 'flex lg:flex-row w-full relative',
    navBarWithOptions: 'flex items-center justify-between md:flex-row flex-wrap',
    navBarWithoutOptions: 'justify-end',
    orgSelectWithNavOptions: 'pr-4 md:pr-8 lg:pr-0 lg:order-3 order-2',
    navRowDivider: ['col-span-3 -mx-8 mt-3', { borderTop: '1px solid lightGrey' }],
    navRowInvite: 'pt-2 pl-2 flex items-center whitespace-nowrap',
    navRowTabs: 'pt-2 min-w-0',
    navRowOrg: 'pt-2 w-full max-w-[300px] justify-self-end flex items-center justify-end min-w-0',
    dropdownOption: [
        'block mb-4 px-5 no-underline capitalize text-brand font-semibold text-base hover:underline focus:underline',
        { textWrap: 'nowrap' },
    ],
    banner: [
        'w-full h-[69px] md:h-12 justify-center hidden flex-col md:flex-row items-center', // md:flex
        {
            background: 'linear-gradient(90deg, #F15A29 -7.95%, #E6126A 16.61%, #7E3F97 100%)',
            '@media (min-width: 769px)': {
                display: 'flex',
            },
        },
    ],
    bannerCopy: ['text-white font-semibold text-center text-[14px] md:text-[15px]'],
    bannerLink: [
        'underline text-white font-bold text-center text-[14px] md:text-[15px] flex flex-row items-center',
    ],
});

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType[number];
const routes = [Routes.Shop, Routes.ShopOnline, Routes.ShopGiftCards, Routes.ShopDiscover] as const;
type NavOptionsRoute = ArrayElement<typeof routes>;

interface UnAuthMenuItem {
    id: string;
    label: string;
    url: string;
    menuItems?: { id: string; label: string; url: string }[];
}

const raiseRightUnAuthMenuItems: UnAuthMenuItem[] = [
    {
        id: 'menu-how-it-works',
        label: 'How It Works',
        url: Routes.HowItWorks,
        menuItems: [
            {
                id: 'how-it-works',
                label: 'How It Works',
                url: Routes.HowItWorks,
            },
            {
                id: 'gift-cards',
                label: 'Gift Cards',
                url: Routes.HowItWorksGiftCards,
            },
            {
                id: 'shop-online',
                label: 'Shop Online',
                url: Routes.HowItWorksShopOnline,
            },
            ...(IS_FLIPGIVE
                ? []
                : [
                      {
                          id: 'local-dining',
                          label: 'Local Dining',
                          url: Routes.HowItWorksLocalDining,
                      },
                  ]),
            // FEC-2567: Hide Book Travel under How It Works for FlipGive. Remove this guard to restore.
            ...(IS_FLIPGIVE
                ? []
                : [
                      {
                          id: 'book-travel',
                          label: 'Book Travel',
                          url: Routes.HowItWorksBookTravel,
                      },
                  ]),
            {
                id: 'browser-extension',
                label: 'Browser Extension',
                url: Routes.HowItWorksBrowserExtension,
            },
            {
                id: 'estimate-your-earnings',
                label: 'Estimate Your Earnings',
                url: Routes.HowItWorksEstimateCalculator,
            },
        ],
    },
    {
        id: 'brands',
        label: 'Brands',
        url: Routes.Brands,
    },
    {
        id: 'menu-who-we-help',
        label: 'Who We Help',
        url: Routes.WhoWeHelp,
        menuItems: [
            {
                id: 'who-we-help',
                label: 'Who We Help',
                url: Routes.WhoWeHelp,
            },
            {
                id: 'schools',
                label: 'Schools',
                url: Routes.WhoWeHelpSchools,
            },
            {
                id: 'churches',
                label: 'Churches',
                url: Routes.WhoWeHelpChurchers,
            },
            {
                id: 'sports-teams',
                label: 'Sports Teams',
                url: Routes.WhoWeHelpSportsTeams,
            },
            {
                id: 'bands',
                label: 'Bands & Music Programs',
                url: Routes.WhoWeHelpBands,
            },
            {
                id: 'booster-clubs',
                label: 'Booster Clubs',
                url: Routes.WhoWeHelpBoosterClubs,
            },
            {
                id: 'nonprofits',
                label: 'Nonprofits',
                url: Routes.WhoWeHelpNonProfit,
            },
        ],
    },
    {
        id: 'about',
        label: 'About',
        url: Routes.AboutUs,
    },
    {
        id: 'menu-learn',
        label: 'Learn',
        url: '',
        menuItems: [
            {
                id: 'coordinator-resources',
                label: 'Coordinator Resources',
                url: Routes.SupportOrganization,
            },
            {
                id: 'participant-resources',
                label: 'Participant Resources',
                url: Routes.SupportParticipant,
            },
            {
                id: 'fundraising-ideas',
                label: 'Fundraising Ideas',
                url: Routes.FundraisingIdeas,
            },
            {
                id: 'fundraising-strategy',
                label: 'Fundraising Strategy',
                url: Routes.FundraisingStrategy,
            },
            {
                id: 'blog',
                label: 'Blog',
                url: Routes.Blog,
            },
            {
                id: 'impact-stories',
                label: 'Impact Stories',
                url: Routes.ImpactStories,
            },
        ],
    },
];

// FEC-2725: FlipGive resolves its own header navigation (distinct items, order, and
// absolute flipgive.com destinations) rather than inheriting RaiseRight's nav above.
const flipGiveUnAuthMenuItems: UnAuthMenuItem[] = [
    {
        id: 'menu-how-it-works',
        label: 'How It Works',
        url: 'https://www.flipgive.com/how-it-works/',
        menuItems: [
            {
                id: 'gift-cards',
                label: 'Gift Cards',
                url: 'https://www.flipgive.com/how-it-works/gift-card-fundraising',
            },
            {
                id: 'shop-online',
                label: 'Shop Online',
                url: 'https://www.flipgive.com/shop',
            },
            {
                id: 'browser-extension',
                label: 'Browser Extension',
                url: 'https://www.flipgive.com/m/extension',
            },
            {
                id: 'estimate-your-earnings',
                label: 'Estimate Your Earnings',
                url: 'https://www.flipgive.com/m/earnings-calculator',
            },
        ],
    },
    {
        id: 'brands',
        label: 'Brands',
        url: 'https://www.flipgive.com/brands',
    },
    {
        id: 'menu-who-we-help',
        label: 'Who We Help',
        url: 'https://www.flipgive.com/who-we-help',
        menuItems: [
            {
                id: 'sports-teams',
                label: 'Sports Teams',
                url: 'https://www.flipgive.com/who-we-help/sports-teams',
            },
            {
                id: 'bands',
                label: 'Bands & Music Programs',
                url: 'https://www.flipgive.com/who-we-help/bands',
            },
            {
                id: 'schools',
                label: 'Schools & Education',
                url: 'https://www.flipgive.com/who-we-help/schools',
            },
            {
                id: 'scouts',
                label: 'Scouts',
                url: 'https://www.flipgive.com/who-we-help/scouts',
            },
            {
                id: 'churches',
                label: 'Churches & Faith Groups',
                url: 'https://www.flipgive.com/who-we-help/churches',
            },
            {
                id: 'charity-non-profit',
                label: 'Charity & Non-profits',
                url: 'https://www.flipgive.com/who-we-help/charity-non-profit',
            },
        ],
    },
    {
        id: 'about',
        label: 'About',
        url: 'https://www.flipgive.com/about-us/',
    },
    {
        id: 'menu-learn',
        label: 'Learn',
        url: '',
        menuItems: [
            {
                id: 'coordinator-resources',
                label: 'Coordinator Resources',
                url: 'https://www.flipgive.com/resources/organization/',
            },
            {
                id: 'participant-resources',
                label: 'Participant Resources',
                url: 'https://www.flipgive.com/resources/participant/',
            },
            {
                id: 'fundraising-ideas',
                label: 'Fundraising Ideas',
                url: 'https://www.flipgive.com/stories/fundraising-ideas',
            },
            {
                id: 'blog',
                label: 'Blog',
                url: 'https://www.flipgive.com/stories',
            },
            {
                id: 'impact-stories',
                label: 'Team Spotlight',
                url: 'https://www.flipgive.com/about-us/team-spotlight',
            },
        ],
    },
];

export const unAuthMenuItems = IS_FLIPGIVE ? flipGiveUnAuthMenuItems : raiseRightUnAuthMenuItems;

function shouldHideHeaderSignUp(pathname: string): boolean {
    return pathname.startsWith('/enroll');
}

const useAddCookieForMarketing = (homeDetails: HomeDetailsResponse, accountRole: string) => {
    /**
     * Temporary addition of a cookie to allow the Marketing Hubspot site header
     * to access a few pieces of user data for display purposes only.
     */
    useEffect(() => {
        const cookies = new Cookies();
        cookies.set(
            'rr_user_info',
            {
                first_name: homeDetails?.profile.firstName,
                organization_name: homeDetails?.defaultOrganization?.name,
                role: accountRole,
            },
            { path: '/' }
        );
    }, [homeDetails]);
};

export const SiteHeader = () => {
    const { userId, role } = useRootSelector((s: FeaturesState) => s.store.account);
    const dispatch = useDispatch<any>();
    const history = useHistory();
    const isAffiliateEnabled = useRootSelector(selectAffiliateEnabledWeb);
    const homeDetails = useRootSelector(selectHomeDetails);
    const homeDetailsAsyncState = useRootSelector((s) => selectHomeDetailsAsyncState(s));
    const { pathname } = useLocation();
    const isJoinProgramPage = pathname === Routes.EnrollFamily;
    const [selectedTab, setSelectedTab] = useState<NavOptionsRoute>();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isP2pEnabled =
        useRootSelector(selectIsP2PEnabled) &&
        Boolean(homeDetails?.defaultOrganization?.isP2PInviteEnabled);

    useEffect(() => {
        const matchedPath = matchPath(pathname, [...routes]);
        if (isAffiliateEnabled && matchedPath && matchedPath.isExact) {
            setSelectedTab(matchedPath.path as NavOptionsRoute);
        } else {
            setSelectedTab(undefined);
        }
    }, [isAffiliateEnabled, pathname]);

    useAddCookieForMarketing(homeDetails, role);

    const applySearchTerm = (term: string) => {
        dispatch(applyTerm(term));

        if (history.location.pathname !== Routes.Brands) {
            history.push(Routes.Brands);
        }
    };

    const navigateToProductDetailPage = (item: Brand) => {
        if (item.id) {
            dispatch(updateSearch({ term: item.name }));
            if (item.isLocalProduct) {
                const [, , id] = item.id.split('-');
                history.push(getPath(Routes.ShopLocalProductDetails, { productId: id }));
            } else {
                const idWithSlug = item.urlSlug
                    ? `${item.id}-${item.urlSlug.replace(/\//g, '')}`
                    : item.id;
                history.push(getPath(Routes.BrandsDetails, { id: idWithSlug }));
            }
        }
    };

    const headerLayoutStyle = userId ? styles.headerContainer : styles.unauthHeaderContainer;
    const redirectRoute = history.location.pathname.startsWith(
        Routes.ShopAccountSettingsCoordinatorTab
    )
        ? Routes.CoordinatorDashboardRoot
        : Routes.Brands;

    const hideHeaderSignUp = shouldHideHeaderSignUp(pathname);

    if (!userId) {
        return (
            <div className={styles.container} id="main-header">
                <div className={headerLayoutStyle}>
                    <div className={styles.logoContainer}>
                        <div className={styles.unAuthBigLogo}>
                            <a
                                href={'/'}
                                className={IS_FLIPGIVE ? styles.flipGiveLogoSize : styles.logoSize}
                            >
                                {IS_FLIPGIVE ? (
                                    <FlipGiveLogo className="w-full h-full" />
                                ) : (
                                    <RaiseRightLogo className="h-full" />
                                )}
                            </a>
                        </div>
                        <div className={styles.unAuthItemsContainer}>
                            {unAuthMenuItems.map((item) => (
                                <div className="relative group cursor-pointer" key={item.id}>
                                    {item.url === '' ? (
                                        <span className={styles.headerLink}>
                                            {item.label}
                                            {item.menuItems && item.menuItems.length > 0 && (
                                                <ChevronDown
                                                    className="chevron-icon"
                                                    color={colors.bonusPink}
                                                    size={20}
                                                />
                                            )}
                                        </span>
                                    ) : (
                                        <a className={styles.headerLink} href={item.url}>
                                            {item.label}
                                            {item.menuItems && item.menuItems.length > 0 && (
                                                <ChevronDown
                                                    className="chevron-icon"
                                                    color={colors.bonusPink}
                                                    size={20}
                                                />
                                            )}
                                        </a>
                                    )}

                                    <div className="z-50 absolute group-hover:visible hover:visible invisible bg-white shadow-lg pt-5 pb-1">
                                        {item.menuItems &&
                                            item.menuItems.length > 0 &&
                                            item.menuItems.map((subItem) =>
                                                item.label === subItem.label ? null : (
                                                    <div
                                                        key={subItem.id}
                                                        className="py-[0.6875rem]"
                                                    >
                                                        <a
                                                            className={styles.dropdownOption}
                                                            href={subItem.url}
                                                        >
                                                            <div className="flex items-center">
                                                                <div className="mr-2.5">
                                                                    {subItem.label}
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </div>
                                                )
                                            )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.signUpContainer + ' lg:w-auto'}>
                        {!hideHeaderSignUp && (
                            <div className="flex items-center w-full lg:w-auto pr-4 xl:pr-4">
                                <Link
                                    className={styles.signUpLink}
                                    to={Routes.EnrollWithoutEnrollCode}
                                >
                                    Sign Up
                                </Link>
                            </div>
                        )}
                        <div className="header_nav_group">
                            <NavButtons setMobileMenuOpen={setMobileMenuOpen} />
                        </div>
                    </div>
                </div>
                <div
                    className={
                        styles.mobileSignUpContainer +
                        ` items-center mt-2 mx-5 ${
                            isJoinProgramPage ? 'justify-center' : 'justify-end'
                        } `
                    }
                >
                    {!hideHeaderSignUp && (
                        <div className={styles.mobileSignUpLinkContainer}>
                            <Link className={styles.signUpLink} to={Routes.EnrollWithoutEnrollCode}>
                                Sign Up
                            </Link>
                        </div>
                    )}
                    <div className="header_nav_group">
                        <NavButtons setMobileMenuOpen={setMobileMenuOpen} />
                    </div>
                </div>
                <div className="hidden">
                    <div className="px-4">
                        <SearchBar
                            onSearch={applySearchTerm}
                            className="none md:hidden w-full"
                            onItemSelected={navigateToProductDetailPage}
                        />
                    </div>
                </div>
                <UnAuthMobileMenu
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                />
            </div>
        );
    }
    return (
        <div className={styles.container} id="main-header">
            <TopBanner />

            <div className={headerLayoutStyle}>
                <div className={styles.logoContainer}>
                    <div className={styles.authSmallLogo}>
                        <Link
                            to={Routes.Shop}
                            className={IS_FLIPGIVE ? styles.flipGiveSmallIconSize : undefined}
                        >
                            {IS_FLIPGIVE ? (
                                <FlipGiveIcon className="w-full h-full" />
                            ) : (
                                <LogoIconLightBg />
                            )}
                        </Link>
                    </div>
                    <div className={styles.authBigLogo}>
                        <Link
                            to={Routes.Shop}
                            className={IS_FLIPGIVE ? styles.flipGiveLogoSize : styles.logoSize}
                        >
                            {IS_FLIPGIVE ? (
                                <FlipGiveLogo className="w-full h-full" />
                            ) : (
                                <RaiseRightLogo className="h-full" />
                            )}
                        </Link>
                    </div>
                </div>
                <div className="hidden md:flex justify-center">
                    <SearchBar
                        className="header_search_bar w-full"
                        onSearch={applySearchTerm}
                        onItemSelected={navigateToProductDetailPage}
                    />
                </div>
                <div className="header_nav_group">
                    <NavButtons />
                </div>
                {isAffiliateEnabled && (
                    <div className="hidden lg:contents">
                        <div className={styles.navRowDivider} />
                        {homeDetailsAsyncState.loading ? (
                            <>
                                <div />
                                <div />
                                <div className={styles.navRowOrg}>
                                    <div className="flex items-center p-2 pr-0">
                                        <div className="w-14">
                                            <Skeleton />
                                        </div>
                                        <span>&nbsp;-&nbsp;</span>
                                        <div className="w-28">
                                            <Skeleton />
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.navRowInvite}>
                                    {isP2pEnabled ? (
                                        <Link
                                            to={`${Routes.ShopAccountSettingsInviteFriendsFamilyOptionsTab}?source=${P2pSources.Header}`}
                                            className="header_invite_friends_icon no-underline text-brand text-xs"
                                        >
                                            <FontAwesomeIcon icon={faUserPlus} /> Invite Friends &
                                            Family
                                        </Link>
                                    ) : null}
                                </div>
                                <div className={styles.navRowTabs}>
                                    <ShopNavOptions selectedTab={selectedTab} />
                                </div>
                                <div className={styles.navRowOrg}>
                                    <OrgSelectDropdown
                                        homeDetails={homeDetails}
                                        redirectRoute={redirectRoute}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className="block md:hidden mt-2">
                <div className="px-4 flex justify-center">
                    <SearchBar
                        onSearch={applySearchTerm}
                        className="none w-full md:hidden header_search_bar"
                        onItemSelected={navigateToProductDetailPage}
                    />
                </div>
            </div>
            <div className={isAffiliateEnabled ? `${styles.navOptionsContainer} lg:hidden` : ''}>
                {homeDetailsAsyncState.loading ? (
                    <div className="flex items-center p-2 pr-0">
                        <div className="w-14">
                            <Skeleton />
                        </div>
                        <span>&nbsp;-&nbsp;</span>
                        <div className="w-28">
                            <Skeleton />
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            className={`${styles.navBar} ${
                                isAffiliateEnabled
                                    ? styles.navBarWithOptions
                                    : styles.navBarWithoutOptions
                            }`}
                        >
                            <div className={`${isP2pEnabled ? 'block sm:hidden' : 'block'}`}></div>
                            {isP2pEnabled ? (
                                <Link
                                    to={`${Routes.ShopAccountSettingsInviteFriendsFamilyOptionsTab}?source=${P2pSources.Header}`}
                                    className="header_invite_friends_icon hidden sm:inline order-1 pl-4 md:pl-8 lg:pl-0 no-underline text-brand text-xs"
                                    href="#"
                                >
                                    <FontAwesomeIcon icon={faUserPlus} /> Invite Friends & Family
                                </Link>
                            ) : null}
                            {isAffiliateEnabled ? (
                                <ShopNavOptions selectedTab={selectedTab} />
                            ) : (
                                <div className="flex-grow lg:flex-none lg:w-auto lg:order-2 order-3 w-full text-center" />
                            )}
                            <div
                                className={`${
                                    isAffiliateEnabled ? styles.orgSelectWithNavOptions : ''
                                }`}
                            >
                                <OrgSelectDropdown
                                    homeDetails={homeDetails}
                                    redirectRoute={redirectRoute}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const TopBanner = () => {
    const bannerRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const bexBannerFlagEnabled = useSelector(
        (state: FeaturesState) =>
            state.store.experimentalFeatureFlags.flags[FeatureFlagTypes.enable_bex_banner_web]
    );
    const shouldShowBanner = useMemo(() => {
        if (!bexBannerFlagEnabled) {
            return false;
        }

        const tag = document.querySelector('meta#rr-extension-meta');
        return tag?.getAttribute('content') !== 'installed';
    }, [location.pathname, bexBannerFlagEnabled]);

    if (location.pathname.includes('/shop/order')) {
        return null;
    }

    // The Book Travel banner linked to the retiring Access Development portal. The browser-extension
    // banner remains independently controlled by its existing feature flag and installation state.
    if (!shouldShowBanner) {
        return null;
    }

    return (
        <div ref={bannerRef} className={styles.banner}>
            <span className={styles.bannerCopy}>Never miss out on earnings!&nbsp;&nbsp;</span>
            <a
                onClick={() => {
                    gaLogClickTopBanner('browser_extension', bannerRef.current?.innerText || '');
                }}
                href={ExternalLinks.BrowserExtensionBanner}
                className={styles.bannerLink}
                target="_blank"
            >
                Add The {Branding} Earnings Extension
                <ArrowUpRight strokeWidth={3} className="h-4 text-white" />
            </a>
        </div>
    );
};
